import _ from 'lodash';
import {
	ExposureConfigurationResponse,
	PerilConfigurationResponse,
	ProductConfigurationResponse,
} from '../../../api/ConfigApi';
import { CancellationResponse } from '../../../types/cancellations';
import {
	EndorsementResponse,
	EndorsementUpdateRequest,
} from '../../../types/endorsements';
import {
	Exposure,
	ExposureCharacteristics,
	ExposureCreateRequest,
	FieldGroupCreateRequest,
	FieldGroupsByLocatorMap,
	FieldGroupUpdateRequest,
	FieldValuesMap,
	GraceLapseReinstatementResponse,
	Peril,
	PerilCharacteristics,
	PerilCreateRequest,
	Policy2,
	PolicyCharacteristics,
} from '../../../types/policy';
import {
	isExposureActive,
	isTimestampWithinInclusiveRange,
} from '../../../util/util';
import {
	getExposureName,
	getPerilName,
	getUpdatedExposureObj,
} from '../util_sharedEndorsementAndRenewal';

export function getTrimmedEndorsementUpdateObj(
	endorsementUpdateObj: EndorsementUpdateRequest,
	keepRepriceInput: boolean,
) {
	const updateObj: EndorsementUpdateRequest = {
		addExposures: endorsementUpdateObj.addExposures,
		addFieldGroups: endorsementUpdateObj.addFieldGroups,
		endExposures: endorsementUpdateObj.endExposures,
		fieldValues: endorsementUpdateObj.fieldValues,
		newPolicyEndTimestamp: endorsementUpdateObj.newPolicyEndTimestamp,
		newPaymentScheduleName: endorsementUpdateObj.newPaymentScheduleName,
		removeFieldGroups: endorsementUpdateObj.removeFieldGroups,
		reprice: endorsementUpdateObj.reprice,
		startTimestamp: endorsementUpdateObj.startTimestamp,
		updateExposures: endorsementUpdateObj.updateExposures,
		updateFieldGroups: endorsementUpdateObj.updateFieldGroups,
	};
	if (!keepRepriceInput) {
		delete updateObj.reprice;
	}

	return updateObj;
}

interface HasName {
	exposureName?: string;
	fieldName?: string;
	name?: string;
}

interface HasCoverage {
	coverageStartTimestamp?: string;
	coverageEndTimestamp?: string;
	replacedTimestamp?: string;
}

interface FieldCharacteristics {
	fieldGroupsByLocator: FieldGroupsByLocatorMap;
	fieldValues: FieldValuesMap;
}

interface CreateRequestCharacteristics {
	fieldValues?: FieldValuesMap;
	fieldGroups?: FieldGroupCreateRequest[];
}

export function assignTemporaryLocator<T extends HasName>(
	named: T,
	index: number,
): string {
	if (named.fieldName) {
		return `added-fieldgroup-${named.fieldName}-${index}`;
	} else if (named.exposureName) {
		return `added-exposure-${named.exposureName}-${index}`;
	} else if (named.name) {
		return `added-peril-${named.name}-${index}`;
	} else {
		return null;
	}
}

function consolidateAddFieldGroups(
	addFieldGroups: FieldGroupCreateRequest[],
	{ fieldValues, fieldGroupsByLocator }: FieldCharacteristics,
): FieldCharacteristics {
	const modifiedFieldValues = _.cloneDeep(fieldValues);
	const modifiedFieldGroupsByLocator = _.cloneDeep(fieldGroupsByLocator);
	addFieldGroups?.forEach((fg, index) => {
		if (fg) {
			const fgLocator: string = assignTemporaryLocator(fg, index);
			modifiedFieldGroupsByLocator[fgLocator] = fg.fieldValues;
			modifiedFieldValues[fg.fieldName] = modifiedFieldValues[fg.fieldName]
				? [...modifiedFieldValues[fg.fieldName], fgLocator]
				: [fgLocator];
		}
	});
	return {
		fieldValues: modifiedFieldValues,
		fieldGroupsByLocator: modifiedFieldGroupsByLocator,
	};
}

function initializeFieldCharacteristics(
	fieldValues: FieldValuesMap,
): FieldCharacteristics {
	return {
		fieldValues,
		fieldGroupsByLocator: {},
	};
}

export function getCharacteristicsFromCreateRequest<
	T extends CreateRequestCharacteristics,
>(createRequest: T): FieldCharacteristics {
	const { fieldValues, fieldGroups } = createRequest;
	return consolidateAddFieldGroups(
		fieldGroups || [],
		initializeFieldCharacteristics(fieldValues || {}),
	);
}

export type Characteristics =
	| PolicyCharacteristics
	| ExposureCharacteristics
	| PerilCharacteristics;

export function getAmendedFieldData(
	characteristics: Characteristics | FieldCharacteristics,
	state: any,
): FieldCharacteristics {
	const { fieldValues, updateFieldGroups, removeFieldGroups, addFieldGroups } =
		state;
	const modifiedFieldDataPerFieldValueChanges = consolidateFieldValueChanges(
		characteristics,
		fieldValues,
	);
	const modifiedFieldDataPerUpdatedFieldGroups = consolidateUpdateFieldGroups(
		updateFieldGroups,
		modifiedFieldDataPerFieldValueChanges,
	);
	const modifiedFieldDataPerRemovedFieldGroups = consolidateRemoveFieldGroups(
		removeFieldGroups,
		modifiedFieldDataPerUpdatedFieldGroups,
	);
	const modifiedFieldDataPerAddedFieldGroups = consolidateAddFieldGroups(
		addFieldGroups,
		modifiedFieldDataPerRemovedFieldGroups,
	);
	return modifiedFieldDataPerAddedFieldGroups;
}

interface HasValues {
	fieldValues?: FieldValuesMap;
}

export function consolidateFieldValueChanges<T extends HasValues>(
	request: T,
	changes: FieldValuesMap,
): T {
	return _.assign({}, request, {
		fieldValues: {
			...request.fieldValues,
			...changes,
		},
	});
}

function consolidateUpdateFieldGroups(
	updateFieldGroups: FieldGroupUpdateRequest[],
	{ fieldValues, fieldGroupsByLocator }: FieldCharacteristics,
): FieldCharacteristics {
	const modifiedFieldValues = _.cloneDeep(fieldValues);
	const modifiedFieldGroupsByLocator = _.cloneDeep(fieldGroupsByLocator);
	updateFieldGroups?.forEach((updateRequest) => {
		const { fieldGroupLocator } = updateRequest;
		const clonedFieldValues = modifiedFieldGroupsByLocator[fieldGroupLocator];
		modifiedFieldGroupsByLocator[fieldGroupLocator] =
			consolidateFieldValueChanges(
				updateRequest,
				clonedFieldValues,
			).fieldValues;
	});
	return {
		fieldValues: modifiedFieldValues,
		fieldGroupsByLocator: modifiedFieldGroupsByLocator,
	};
}

function consolidateRemoveFieldGroups(
	removeFieldGroups: string[],
	{ fieldValues, fieldGroupsByLocator }: FieldCharacteristics,
): FieldCharacteristics {
	const modifiedFieldValues = _.cloneDeep(fieldValues);
	const modifiedFieldGroupsByLocator = _.cloneDeep(fieldGroupsByLocator);
	removeFieldGroups?.forEach((locator) => {
		delete modifiedFieldGroupsByLocator[locator];
		Object.keys(modifiedFieldValues).forEach((field) => {
			const index = _.findIndex(
				modifiedFieldValues[field],
				(loc) => loc === locator,
			);
			if (index > -1) {
				modifiedFieldValues[field].splice(index, 1);
			}
		});
	});
	return {
		fieldValues: modifiedFieldValues,
		fieldGroupsByLocator: modifiedFieldGroupsByLocator,
	};
}

export function getExposuresForEndorsement(
	effectiveDate: string,
	policy: Policy2,
	exposures: Exposure[],
	cancellations: any[],
	graceLapse: any[],
) {
	// we want to filter out any exposures that were not active at the time of endorsement
	const activeExposuresAtTimeOfEndorsement = exposures.filter((e) => {
		const isActive = isExposureActive(
			e,
			policy,
			effectiveDate,
			cancellations,
			graceLapse,
		);
		return isActive;
	});
	return activeExposuresAtTimeOfEndorsement;
}

export function getPolicyCoveragePeriod(policy: Policy2) {
	const {
		originalContractStartTimestamp: policyStartTimestamp,
		effectiveContractEndTimestamp: policyEndTimestamp,
	} = policy;
	return { policyStartTimestamp, policyEndTimestamp };
}

export function isPerilCoverageEverInForce(peril: Peril) {
	const perilCharacteristics = _.filter(
		peril.characteristics,
		(pc) => !pc.replacedTimestamp,
	);
	if (perilCharacteristics && perilCharacteristics.length) {
		return _.some(
			perilCharacteristics,
			(pc) => pc.coverageStartTimestamp !== pc.coverageEndTimestamp,
		);
	} else {
		return false;
	}
}

export function getExposureCoveragePeriod({ perils }: Exposure) {
	const perilCoveragePeriods = perils
		.filter(isPerilCoverageEverInForce)
		.map((peril) => getPerilCoveragePeriod(peril));
	return getCoveragePeriod(perilCoveragePeriods);
}

function getPerilCoveragePeriod({ characteristics }: Peril) {
	return getCoveragePeriod(characteristics);
}

function getCoveragePeriod<T extends HasCoverage>(covered: T[]) {
	const unreplacedCoverage = covered.filter((c) => !c.replacedTimestamp);
	if (unreplacedCoverage.length > 0) {
		const coverageStartTimestamps = unreplacedCoverage.map(
			(uc) => +uc.coverageStartTimestamp,
		);
		const coverageEndTimestamps = unreplacedCoverage.map(
			(uc) => +uc.coverageEndTimestamp,
		);
		return {
			coverageStartTimestamp: Math.min(...coverageStartTimestamps).toString(),
			coverageEndTimestamp: Math.max(...coverageEndTimestamps).toString(),
		};
	} else {
		return null;
	}
}

export const getPolicyInvalidDateRanges = (
	policyEndTimestamp: number,
	graceLapseReinstatements: GraceLapseReinstatementResponse[],
	cancellations: CancellationResponse[],
): number[][] => {
	let invalidDateRanges: number[][] = [];

	if (graceLapseReinstatements) {
		// Cancellations v1
		invalidDateRanges = graceLapseReinstatements.reduce((acc, current) => {
			const { lapse, reinstatements } = current;

			if (lapse) {
				const lapseTimestamp = parseInt(lapse.lapseTimestamp, 10);
				const sortedReinstatements = reinstatements.sort(
					(a, b) =>
						parseInt(a.createdTimestamp, 10) - parseInt(b.createdTimestamp, 10),
				);

				if (sortedReinstatements.length > 0) {
					const { issuedTimestamp, reinstatementTimestamp, currentStatus } =
						sortedReinstatements[sortedReinstatements.length - 1];

					if (currentStatus !== 'closed' && issuedTimestamp) {
						const reinstatementDate = parseInt(reinstatementTimestamp, 10) - 1;

						return acc.concat([[lapseTimestamp, reinstatementDate]]);
					}
				}

				return acc.concat([[lapseTimestamp, policyEndTimestamp]]);
			}

			return acc;
		}, []);
	}
	if (cancellations) {
		const validCancellations = cancellations
			.filter((c) => c.state === 'issued' || c.state === 'reinstated')
			.sort(
				(a, b) =>
					parseInt(a.createdTimestamp, 10) - parseInt(b.createdTimestamp, 10),
			);

		const badDates = validCancellations.reduce((acc, current) => {
			const cancellationEffectiveDate = parseInt(
				current.effectiveTimestamp,
				10,
			);

			if (current.reinstatement) {
				const reinstatementDate =
					parseInt(current.reinstatement.effectiveTimestamp, 10) - 1;
				return acc.concat([[cancellationEffectiveDate, reinstatementDate]]);
			} else {
				return acc.concat([[cancellationEffectiveDate, policyEndTimestamp]]);
			}
		}, []);

		invalidDateRanges = [...invalidDateRanges, ...badDates];
	}

	invalidDateRanges = invalidDateRanges.sort((a, b) => a[0] - b[0]);

	return invalidDateRanges;
};

/**
 * Returns the urlId to be used in exposure links and exposure display name, this shared function is
 * required for EndorsementSidebar and EndorsementExposures/ExposuresList
 */
export const getExposureDisplayNameAndUrl = (
	exposure: Exposure | ExposureCreateRequest,
	endorsementUpdateObj: EndorsementResponse,
	productConfig: ProductConfigurationResponse,
) => {
	let urlId: string;
	let exposureDisplayName: string;
	let exposureConfig: ExposureConfigurationResponse;

	if (exposure && productConfig && endorsementUpdateObj) {
		const rawExposureName =
			(exposure as Exposure).name ||
			(exposure as ExposureCreateRequest).exposureName;
		exposureConfig = productConfig.policyConfiguration.exposures.find(
			(e) => e.name === rawExposureName,
		);
		const exposureDefaultName = exposureConfig?.displayName
			? exposureConfig.displayName
			: rawExposureName;
		exposureDisplayName = getExposureName(
			exposure,
			exposureDefaultName,
			null,
			endorsementUpdateObj as EndorsementResponse,
		);

		if ((exposure as Exposure).displayId) {
			urlId = (exposure as Exposure).displayId;
		} else {
			// exposure is ExposureCreateRequest
			const { addExposures } = endorsementUpdateObj;
			const exposureIndex = addExposures
				? addExposures.findIndex(
						(exp) =>
							exp.exposureLocator ===
							(exposure as ExposureCreateRequest).exposureLocator,
				  )
				: -1;
			urlId = exposureIndex !== -1 ? `added-${exposureIndex}` : '';
			exposureDisplayName = `Added: ${exposureDisplayName}`;
		}

		// const newExposureIndex = index - currentExposures.length;
		// const urlId = (exposure as Exposure).displayId ? (exposure as Exposure).displayId : `added-${newExposureIndex}`;
	}

	return { urlId, exposureDisplayName, exposureConfig };
};

export const isEndorsementPerilActive = (
	peril: Peril | PerilCreateRequest,
	policy: Policy2,
	endorsementUpdateObj: EndorsementResponse,
) => {
	if (!peril || !policy || !endorsementUpdateObj) return false;

	const { policyStartTimestamp, policyEndTimestamp } =
		getPolicyCoveragePeriod(policy);
	const isEndorsementDateInPolicyTimeRange = isTimestampWithinInclusiveRange(
		endorsementUpdateObj?.startTimestamp?.toString(),
		policyStartTimestamp,
		policyEndTimestamp,
	);

	const isEndorsementDateInPerilTimeRange = (
		peril as Peril
	)?.characteristics?.find(
		(c) =>
			!c.replacedTimestamp &&
			parseInt(endorsementUpdateObj?.startTimestamp, 10) >=
				parseInt(c.coverageStartTimestamp, 10) &&
			parseInt(endorsementUpdateObj?.startTimestamp, 10) <
				parseInt(c.coverageEndTimestamp, 10),
	);

	return (
		isEndorsementDateInPolicyTimeRange && !!isEndorsementDateInPerilTimeRange
	);
};

/**
 * Returns the urlId to be used in exposure links and exposure display name, this shared function is
 * required for EndorsementSidebar and EndorsementExposures/ExposuresList
 */
export const getPerilDisplayNameAndUrl = (
	exposure: Exposure | ExposureCreateRequest,
	peril: Peril | PerilCreateRequest,
	endorsementUpdateObj: EndorsementResponse,
	exposureConfig: ExposureConfigurationResponse,
	policy: Policy2,
) => {
	let urlId: string;
	let perilDisplayName: string;
	let perilConfig: PerilConfigurationResponse;
	let isActive = true;

	if (exposure && peril && exposureConfig && endorsementUpdateObj) {
		const isExposureAdded = !(exposure as Exposure).displayId;
		perilConfig = exposureConfig.perils.find((p) => p.name === peril.name);
		perilDisplayName = getPerilName(
			peril,
			perilConfig.displayName,
			exposure,
			null,
			endorsementUpdateObj,
		);

		if ((peril as Peril).displayId) {
			urlId = (peril as Peril).displayId;

			if (!isExposureAdded) {
				// call isPerilActive only for existing peril on existing exposure
				isActive = isEndorsementPerilActive(
					peril as Peril,
					policy,
					endorsementUpdateObj,
				);
			}
		} else {
			// peril is added, PerilCreateRequest
			let perils;

			if (isExposureAdded) {
				perils = exposure.perils;
			} else {
				const exposureUpdateRequest = getUpdatedExposureObj(
					endorsementUpdateObj,
					(exposure as Exposure).locator,
				);
				perils = exposureUpdateRequest.addPerils;
			}

			const perilIndex = perils
				? perils.findIndex(
						(per: Peril | PerilCreateRequest) =>
							per.locator === (peril as PerilCreateRequest).locator,
				  )
				: -1;
			urlId = perilIndex !== -1 ? `added-${perilIndex}` : '';
			perilDisplayName = `Added: ${perilDisplayName}`;
		}

		if (!isActive) {
			perilDisplayName += ' (Inactive)';
		}
	}

	return { urlId, perilDisplayName, perilConfig, isActive };
};
